import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSecureAdminAuth } from "@/hooks/useSecureAdminAuth";
import { Helmet } from "react-helmet-async";
import {
  Users,
  IndianRupee,
  ShoppingCart,
  TrendingUp,
  Calendar,
  BookOpen,
  FileText,
  Heart,
  RefreshCw,
  Crown,
  Clock,
  UserPlus,
  Youtube,
  Newspaper,
  Globe,
  Landmark,
  SpellCheck,
  MessageSquareQuote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

type Profile = {
  id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  is_premium: boolean;
  country_name: string | null;
  phone_number: string | null;
};

type Purchase = {
  id: string;
  user_id: string;
  amount: number;
  amount_inr: number;
  amount_usd: number;
  currency: string | null;
  status: string;
  payment_from: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
};

interface ContentCounts {
  stories: number;
  printables: number;
  helpers: number;
  blogs: number;
  videos: number;
  worldItems: number;
  historyItems: number;
  spellings: number;
  sentences: number;
}

const emptyContent: ContentCounts = {
  stories: 0,
  printables: 0,
  helpers: 0,
  blogs: 0,
  videos: 0,
  worldItems: 0,
  historyItems: 0,
  spellings: 0,
  sentences: 0,
};

/** Revenue in INR for a purchase (uses amount_inr, falls back to amount for INR rows). */
const inrOf = (p: Purchase) => {
  const inr = Number(p.amount_inr) || 0;
  if (inr > 0) return inr;
  return (p.currency || "").toUpperCase() === "INR" ? Number(p.amount) || 0 : 0;
};

const usdOf = (p: Purchase) => {
  const usd = Number(p.amount_usd) || 0;
  if (usd > 0) return usd;
  return (p.currency || "").toUpperCase() === "USD" ? Number(p.amount) || 0 : 0;
};

const monthKey = (d: string) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (key: string) =>
  new Date(key + "-01").toLocaleDateString("en-IN", { month: "short", year: "numeric" });

const lastSixMonthKeys = () => {
  const keys: string[] = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
};

const inr = (n: number) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
const usd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const AdminOverview = () => {
  const { adminUser } = useSecureAdminAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [content, setContent] = useState<ContentCounts>(emptyContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { toast } = useToast();

  const countActive = async (table: string, activeCol: string | null = "is_active") => {
    let q = (supabase as any).from(table).select("id", { count: "exact", head: true });
    if (activeCol) q = q.eq(activeCol, true);
    const { count, error } = await q;
    if (error) {
      console.error(`Count error for ${table}:`, error);
      return 0;
    }
    return count || 0;
  };

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sessionToken =
        typeof window !== "undefined" ? localStorage.getItem("admin_session_token") || "" : "";

      const [usersRes, counts] = await Promise.all([
        supabase.functions.invoke("admin-get-users", {
          headers: { "x-admin-session-token": sessionToken },
          body: {
            adminUser: adminUser ? { username: adminUser.username, email: adminUser.email } : undefined,
          },
        }),
        Promise.all([
          countActive("stories"),
          countActive("printable_items"),
          countActive("community_helpers"),
          countActive("blogs", "is_published"),
          countActive("youtube_gallery"),
          countActive("world_items"),
          countActive("history_items"),
          countActive("spellings"),
          countActive("sentences_of_the_day"),
        ]),
      ]);

      if (usersRes.error) throw usersRes.error;

      setProfiles((usersRes.data?.profiles as Profile[]) || []);
      setPurchases((usersRes.data?.purchases as Purchase[]) || []);
      const [stories, printables, helpers, blogs, videos, worldItems, historyItems, spellings, sentences] = counts;
      setContent({ stories, printables, helpers, blogs, videos, worldItems, historyItems, spellings, sentences });
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error("Error fetching dashboard stats:", err);
      const msg = err?.message || "Failed to load dashboard data";
      setError(msg);
      toast({
        title: "Error",
        description: "Failed to load dashboard statistics: " + msg + ". Try signing out and back in.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUser?.username, adminUser?.email]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // ---------- Derived stats ----------
  const now = new Date();
  const completed = purchases.filter((p) => p.status === "completed");
  const pending = purchases.filter((p) => p.status === "pending");
  const paidCompleted = completed.filter((p) => inrOf(p) > 0 || usdOf(p) > 0);
  const revenueINR = completed.reduce((s, p) => s + inrOf(p), 0);
  const revenueUSD = completed.reduce((s, p) => s + usdOf(p), 0);
  const activeSubs = completed.filter((p) => p.end_date && new Date(p.end_date) > now).length;
  const premiumUsers = profiles.filter((p) => p.is_premium).length;
  const payingUserIds = new Set(paidCompleted.map((p) => p.user_id));
  const conversion = profiles.length > 0 ? (payingUserIds.size / profiles.length) * 100 : 0;
  const thisMonth = monthKey(now.toISOString());
  const newUsersThisMonth = profiles.filter((p) => monthKey(p.created_at) === thisMonth).length;
  const sevenDaysAgo = now.getTime() - 7 * 24 * 60 * 60 * 1000;
  const newUsers7d = profiles.filter((p) => new Date(p.created_at).getTime() >= sevenDaysAgo).length;

  const months = lastSixMonthKeys();
  const monthlyRevenue = months.map((k) => ({
    month: monthLabel(k),
    revenue: completed.filter((p) => monthKey(p.created_at) === k).reduce((s, p) => s + inrOf(p), 0),
  }));
  const userGrowth = months.map((k) => ({
    month: monthLabel(k),
    users: profiles.filter((p) => monthKey(p.created_at) === k).length,
  }));

  const profileMap = new Map(profiles.map((p) => [p.id, p]));
  const recentPurchases = [...purchases]
    .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
    .slice(0, 6);
  const recentUsers = [...profiles]
    .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
    .slice(0, 6);

  const totalContent =
    content.stories +
    content.printables +
    content.helpers +
    content.blogs +
    content.videos +
    content.worldItems +
    content.historyItems +
    content.spellings +
    content.sentences;

  if (error && profiles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="text-red-600 text-center">
          <h3 className="text-lg font-semibold mb-2">Failed to Load Dashboard</h3>
          <p className="text-sm">{error}</p>
          <p className="text-xs text-gray-500 mt-2">If this keeps happening, sign out of the admin panel and sign back in.</p>
        </div>
        <Button onClick={fetchDashboardStats} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  if (loading && profiles.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const statusClass = (s: string) =>
    s === "completed"
      ? "bg-green-100 text-green-700"
      : s === "pending"
      ? "bg-amber-100 text-amber-700"
      : "bg-red-100 text-red-700";

  return (
    <div className="space-y-6">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Admin Overview
          </h2>
          <p className="text-gray-600">
            Essential statistics and insights for TinyMinds platform
            {lastUpdated && (
              <span className="text-xs text-gray-400 ml-2">
                · Updated {lastUpdated.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={fetchDashboardStats}
          disabled={loading}
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700">Total Users</CardTitle>
            <Users className="h-6 w-6 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-800">{profiles.length.toLocaleString("en-IN")}</div>
            <p className="text-xs text-blue-600 mt-1">
              +{newUsersThisMonth} this month · +{newUsers7d} last 7 days
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700">Total Revenue</CardTitle>
            <IndianRupee className="h-6 w-6 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-800">{inr(revenueINR)}</div>
            <p className="text-xs text-green-600 mt-1">
              From completed purchases{revenueUSD > 0 ? ` · ${usd(revenueUSD)} USD` : ""}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-700">Successful Purchases</CardTitle>
            <ShoppingCart className="h-6 w-6 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-800">{completed.length}</div>
            <p className="text-xs text-purple-600 mt-1">
              {paidCompleted.length} paid · {completed.length - paidCompleted.length} granted by admin · {pending.length} pending
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-orange-700">Conversion Rate</CardTitle>
            <TrendingUp className="h-6 w-6 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-800">{conversion.toFixed(1)}%</div>
            <p className="text-xs text-orange-600 mt-1">
              {payingUserIds.size} paying of {profiles.length} users
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Subscription stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-medium text-amber-700">Premium Users</p>
              <p className="text-2xl font-bold text-amber-800">{premiumUsers}</p>
              <p className="text-xs text-amber-600">Marked premium on profile</p>
            </div>
            <Crown className="h-7 w-7 text-amber-500" />
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-medium text-teal-700">Active Subscriptions</p>
              <p className="text-2xl font-bold text-teal-800">{activeSubs}</p>
              <p className="text-xs text-teal-600">Plans not yet expired</p>
            </div>
            <Calendar className="h-7 w-7 text-teal-500" />
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-medium text-rose-700">Pending Payments</p>
              <p className="text-2xl font-bold text-rose-800">{pending.length}</p>
              <p className="text-xs text-rose-600">
                {inr(pending.reduce((s, p) => s + inrOf(p), 0))} awaiting confirmation
              </p>
            </div>
            <Clock className="h-7 w-7 text-rose-500" />
          </CardContent>
        </Card>
      </div>

      {/* Content Library */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-500" />
              Content Library
            </span>
            <span className="text-sm font-normal text-gray-500">{totalContent} live items</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: "Stories", value: content.stories, icon: BookOpen, cls: "bg-yellow-50 text-yellow-800 border-yellow-200" },
              { label: "Printables", value: content.printables, icon: FileText, cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
              { label: "Community Helpers", value: content.helpers, icon: Heart, cls: "bg-pink-50 text-pink-800 border-pink-200" },
              { label: "Blog Posts", value: content.blogs, icon: Newspaper, cls: "bg-sky-50 text-sky-800 border-sky-200" },
              { label: "YouTube Videos", value: content.videos, icon: Youtube, cls: "bg-red-50 text-red-800 border-red-200" },
              { label: "World Items", value: content.worldItems, icon: Globe, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
              { label: "History Items", value: content.historyItems, icon: Landmark, cls: "bg-orange-50 text-orange-800 border-orange-200" },
              { label: "Spellings", value: content.spellings, icon: SpellCheck, cls: "bg-violet-50 text-violet-800 border-violet-200" },
              { label: "Magic Words", value: content.sentences, icon: MessageSquareQuote, cls: "bg-cyan-50 text-cyan-800 border-cyan-200" },
            ].map(({ label, value, icon: Icon, cls }) => (
              <div key={label} className={`rounded-xl border p-3 ${cls}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium opacity-80">{label}</p>
                  <Icon className="h-4 w-4 opacity-70" />
                </div>
                <p className="text-2xl font-bold mt-1">{value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Monthly Revenue (last 6 months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip formatter={(value) => [inr(Number(value)), "Revenue"]} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#10B981"
                  strokeWidth={3}
                  dot={{ fill: "#10B981", strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              New Users (last 6 months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Bar dataKey="users" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-purple-500" />
              Recent Purchases
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentPurchases.length > 0 ? (
              <div className="space-y-2">
                {recentPurchases.map((p) => {
                  const u = profileMap.get(p.user_id);
                  const amountINR = inrOf(p);
                  const amountUSD = usdOf(p);
                  return (
                    <div key={p.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {u?.full_name || u?.email || `User ${p.user_id.substring(0, 8)}`}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {u?.email && u?.full_name ? `${u.email} · ` : ""}
                          {new Date(p.created_at).toLocaleDateString("en-IN")}
                          {p.payment_from ? ` · ${p.payment_from}` : ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-green-600">
                          {amountINR > 0 ? inr(amountINR) : amountUSD > 0 ? usd(amountUSD) : "Free"}
                        </p>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${statusClass(p.status)}`}>
                          {p.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No purchases yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-500" />
              Newest Sign-ups
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentUsers.length > 0 ? (
              <div className="space-y-2">
                {recentUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{u.full_name || u.email}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {u.full_name ? `${u.email} · ` : ""}
                        {u.country_name || "—"}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-500">{new Date(u.created_at).toLocaleDateString("en-IN")}</p>
                      {u.is_premium && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Premium</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No users yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverview;
